import { useEffect, useState } from 'react';
import '../Admin.scss';
import axios from 'axios';
import MainURL from '../../MainURL';
import Pagination from '../../components/Pagination';
import Loading from '../../components/Loading';
import RecruitSavedSearchBar from './RecruitSavedSearchBar';
import RecruitSavedListTable from './RecruitSavedListTable';
import RecruitSavedEditModal from './RecruitSavedEditModal';
import RecruitSavedMoveModal from './RecruitSavedMoveModal';
import type { RecruitSavedListItem } from './recruitSavedAdminShared';
import {
  MINISTER_RECRUIT_CONFIG,
  OVERSEAS_RECRUIT_CONFIG,
} from '../../screens/recruit/common/recruitConfigs';

type Props = {
  /** 해외사역지 공고 관리 (recruitMinisterOverseas) */
  overseas?: boolean;
};

export default function RegisterRecruitMinister({ overseas = false }: Props) {
  const config = overseas ? OVERSEAS_RECRUIT_CONFIG : MINISTER_RECRUIT_CONFIG;
  const boardKey = overseas ? 'overseas' : 'minister';
  const adminListUrl = (page: number) =>
    overseas ? `${MainURL}/recruitoverseas/admin/getrecruit/${page}` : `${MainURL}/recruitwork/getrecruit/${page}`;
  const adminDeleteUrl = overseas ? `${MainURL}/recruitoverseas/admin/deleterecruit` : `${MainURL}/recruitwork/deleterecruit`;

  const [list, setList] = useState<RecruitSavedListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [activeSearchWord, setActiveSearchWord] = useState('');
  const [editItem, setEditItem] = useState<RecruitSavedListItem | null>(null);
  const [moveItem, setMoveItem] = useState<RecruitSavedListItem | null>(null);

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = activeSearchWord
        ? await axios.post(`${MainURL}/${config.apiBase}/recruitsearchunified`, {
            searchWord: activeSearchWord,
          })
        : await axios.get(adminListUrl(currentPage));

      const resultData = res.data?.resultData;
      const rows = Array.isArray(resultData) ? resultData : resultData ? [resultData] : [];

      setList(rows);
      setTotalCount(res.data?.totalCount || rows.length);
    } catch (error) {
      console.error('구인 목록 조회 오류:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPosts();
  }, [currentPage, activeSearchWord, overseas]);

  const deletePost = async (item: RecruitSavedListItem) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const res = await axios.post(adminDeleteUrl, {
        id: item.id,
      });

      if (res.data?.success) {
        alert('삭제되었습니다.');
        void fetchPosts();
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleSearch = () => {
    setActiveSearchWord(searchWord.trim());
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearchWord('');
    setActiveSearchWord('');
    setCurrentPage(1);
  };

  return (
    <div className="admin-board-manage recruit-minister-saved-manage">
      <p className="admin-board-manage__desc">
        {overseas ? '해외사역지' : '국내사역지'} 저장된 구인 공고를 조회·수정·삭제·다른 게시판으로 이동할 수 있습니다.{' '}
        <a href={config.listPath} target="_blank" rel="noreferrer">
          사이트 게시판 보기
        </a>
      </p>

      <RecruitSavedSearchBar
        searchWord={searchWord}
        onSearchWordChange={setSearchWord}
        onSearch={handleSearch}
        onReset={handleResetSearch}
      />

      {loading ? (
        <Loading />
      ) : (
        <>
          <RecruitSavedListTable
            items={list}
            totalCount={totalCount}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            isSearchMode={Boolean(activeSearchWord)}
            onView={(item) =>
              window.open(`${config.detailPath}/${item.id}`, '_blank', 'noopener,noreferrer')
            }
            onEdit={(item) => setEditItem(item)}
            onMove={(item) => setMoveItem(item)}
            onDelete={deletePost}
          />

          {editItem?.id != null ? (
            <RecruitSavedEditModal
              recruitType={boardKey}
              apiBase={config.apiBase}
              itemId={editItem.id}
              userAccount={editItem.userAccount}
              onClose={() => setEditItem(null)}
              onSaved={() => {
                setEditItem(null);
                void fetchPosts();
              }}
            />
          ) : null}

          <RecruitSavedMoveModal
            sourceBoard={boardKey}
            item={moveItem}
            onClose={() => setMoveItem(null)}
            onMoved={() => void fetchPosts()}
          />

          {!activeSearchWord && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
}
